import {
    isTransactionColumn,
    TransactionView,
} from "@common/types/views/transactionViews";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { saveBlobResponse, useFileDownload } from "@lib/fileDownload";
import React, { useContext, useMemo } from "react";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "@components/transactions/PaginatedTransactionsTable.context";
import { RulesSuggestionToggle } from "@components/transactions/rulesSuggestion/RulesSuggestionToggle";
import { useEntities } from "@hooks/useEntities";
import { getTransactionsCsv } from "@lib/transactions";
import { convertFiltersToDto } from "@components/transactions/useTransactionsQuery";
import { getSortExpression } from "@components/transactions/useSort";
import { AccountsFilterEntityValueDisplay } from "@components/financialAccount/AccountsFilter/AccountsFilterEntityValueDisplay";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { DownloadIcon } from "@icons";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useTransactionViewConfig } from "@components/transactions/views/useTransactionViewConfig";
import { useRuleTransactionCounts } from "@api/rules.api";
import { HStack } from "@design-system/HStack/HStack";
import css from "./RuleTransactionsDrawerHeader.module.scss";

type PreviewMode = "matching" | "applied";

interface RuleTransactionsDrawerHeaderProps {
    defaultView: TransactionView;
    ruleId?: string;
}

export const RuleTransactionsDrawerHeader: React.FC<
    RuleTransactionsDrawerHeaderProps
> = ({ defaultView, ruleId }) => {
    const activeWorkspace = useActiveWorkspace();
    const {
        filters,
        currentView,
        currentSort,
        isMatchingRuleMode,
        matchingRuleTotal,
    } = useContext(PaginatedTransactionsDataContext);
    const { setCurrentView, setIsMatchingRuleMode } = useContext(
        PaginatedTransactionsActionsContext,
    );

    const transactionCounts = useRuleTransactionCounts();
    const appliedCount =
        ruleId && transactionCounts.data
            ? (transactionCounts.data[ruleId] ?? 0)
            : 0;

    const previewMode: PreviewMode = isMatchingRuleMode
        ? "matching"
        : "applied";

    const matchingCountSuffix =
        matchingRuleTotal != null && matchingRuleTotal > 0
            ? ` (${matchingRuleTotal})`
            : "";
    const matchingLabel = `Matching conditions${matchingCountSuffix}`;
    const appliedLabel = `Was applied to (${appliedCount})`;
    const previewModeItems: Array<PickerItemData<PreviewMode>> = [
        { value: "matching", label: matchingLabel },
        { value: "applied", label: appliedLabel },
    ];

    const entities = useEntities({
        onlyBusiness: true,
        excludeMockEntities: true,
    });

    const selectedEntities = useMemo(
        () => entities.filter((e) => filters.entityIds?.includes(e.id)),
        [entities, filters.entityIds],
    );

    const { download: exportTransactions, isDownloading: isExporting } =
        useFileDownload(async () => {
            if (!activeWorkspace) {
                return;
            }

            const response = await getTransactionsCsv({
                workspaceId: activeWorkspace.id,
                filters: convertFiltersToDto(filters, entities),
                columns: currentView.columns
                    .map((column) => column.id)
                    .filter(isTransactionColumn),
                sort: getSortExpression(currentSort),
            });

            saveBlobResponse(response, "transactions.csv");
        });

    const transactionsViewConfig = useTransactionViewConfig();

    return (
        <div className={css.filters}>
            <HStack gap={12} align="center">
                {ruleId ? (
                    <Picker.Select
                        items={previewModeItems}
                        value={previewMode}
                        onValueChange={(value) => {
                            if (value !== null) {
                                setIsMatchingRuleMode(value === "matching");
                            }
                        }}
                        trigger={
                            <Picker.Button variant="secondary" size="sm" />
                        }
                    />
                ) : (
                    <div className={css.entityContainer}>
                        <span className={css.label}>
                            <AccountsFilterEntityValueDisplay
                                everythingSelected={false}
                                selectedEntities={selectedEntities}
                                singleEntityName
                                showSelectedIcons
                            />
                        </span>
                    </div>
                )}
            </HStack>
            <section className={css.actions}>
                <RulesSuggestionToggle />
                <ButtonWithLoader
                    variant="primary"
                    size="md"
                    iconOnly
                    aria-label="Export transactions"
                    onClick={() => {
                        void exportTransactions();
                    }}
                    loading={isExporting}
                >
                    <DownloadIcon />
                </ButtonWithLoader>
                <ColumnSettings
                    defaultView={defaultView}
                    viewConfig={transactionsViewConfig}
                    view={currentView}
                    onViewChange={setCurrentView}
                />
            </section>
        </div>
    );
};
